import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { TPlani8State } from '../reducers';
import * as TTnaUpdateActions from './tna-approvals.actions';
import * as TTnaUpdateSelectors from './tna-approvals.selectors';
import {
  TApprovalType,
  TREQUEST_CREATE_TNA_STEP_APPROVAL_REQUEST,
  TREQUEST_GET_TNA_STEP_APPROVAL_LIST,
  TREQUEST_GET_TNA_STEP_APPROVAL_PENDING_LIST,
  TREQUEST_UPDATE_TNA_STEP_APPROVAL,
  TRESPONSE_GET_TNA_STEP_APPROVAL_LIST,
  TRESPONSE_GET_TNA_STEP_APPROVAL_PENDING_LIST,
  TTnaOrderStepFetchReason,
  TUserPaginationOption,
} from '@monorepo/types';
import { AppNotificationUtilClass, Plani8DataService } from '@monorepo/services';
import { lastValueFrom } from 'rxjs';

export const TNA_APPROVALS_ITEMS_PER_PAGE_LIST = [10, 50, 100, 200];

@Injectable({
  providedIn: 'root',
})
export class TnaApprovalsFacade {
  constructor(
    private store: Store<TPlani8State>,
    private appNotificationUtilClass: AppNotificationUtilClass,
    private plani8DataService: Plani8DataService,
  ) {}

  fetchTnaStepApprovalList(payload: TREQUEST_GET_TNA_STEP_APPROVAL_LIST, fetchReason: TTnaOrderStepFetchReason) {
    this.store.dispatch(new TTnaUpdateActions.GetTnaStepApprovalList(payload, fetchReason));
  }

  fetchTnaStepApprovalDetails(tosId: TRESPONSE_GET_TNA_STEP_APPROVAL_LIST['tnaOrderStepItemList'][number]['tosId']) {
    this.store.dispatch(new TTnaUpdateActions.GetTnaStepApprovalDetails(tosId));
  }

  fetchTnaStepApprovalPendingList(
    payload: TREQUEST_GET_TNA_STEP_APPROVAL_PENDING_LIST,
    fetchReason: TTnaOrderStepFetchReason,
  ) {
    this.store.dispatch(new TTnaUpdateActions.GetTnaStepApprovalPendingList(payload, fetchReason));
  }

  fetchTnaStepApprovalPendingDetails(
    approvalId: TRESPONSE_GET_TNA_STEP_APPROVAL_PENDING_LIST['stepApprovalPendingItems'][number]['tnaStepApprovalId'],
  ) {
    this.store.dispatch(new TTnaUpdateActions.GetTnaStepApprovalPendingDetails(approvalId));
  }

  async fetchEmployeesForTnaApproval(approvalType: TApprovalType) {
    const loading = await this.appNotificationUtilClass.presentLoading('Loading', 3000);
    return lastValueFrom(this.plani8DataService.fetchEmployeedForTnaSTepApproval(approvalType)).finally(() => {
      this.appNotificationUtilClass.dismissLoadingV2(loading);
    });
  }

  requestTnaStepApproval(
    payload: TREQUEST_CREATE_TNA_STEP_APPROVAL_REQUEST,
    listingPayload: TREQUEST_GET_TNA_STEP_APPROVAL_LIST,
  ) {
    this.store.dispatch(new TTnaUpdateActions.TnaStepApprovalRequest(payload, listingPayload));
  }

  updateTnaStepApproval(
    payload: TREQUEST_UPDATE_TNA_STEP_APPROVAL,
    listingPayload: TREQUEST_GET_TNA_STEP_APPROVAL_PENDING_LIST,
  ) {
    this.store.dispatch(new TTnaUpdateActions.UpdateTnaStepApproval(payload, listingPayload));
  }

  getTnaStepApprovalList() {
    return this.store.pipe(select(TTnaUpdateSelectors.getTnaStepApprovalList));
  }

  getTnaStepApprovalDetails() {
    return this.store.pipe(select(TTnaUpdateSelectors.getTnaStepApprovalDetails));
  }

  getTnaStepApprovalPendingList() {
    return this.store.pipe(select(TTnaUpdateSelectors.getTnaSTepApprovalPendingList));
  }

  getTnaStepApprovalPendingDetails() {
    return this.store.pipe(select(TTnaUpdateSelectors.getTnaStepApprovaPendinglDetails));
  }

  getUserPaginationOption() {
    return this.store.pipe(select(TTnaUpdateSelectors.getUserPaginationOption));
  }

  setUserPaginationOption(option: TUserPaginationOption) {
    return this.store.dispatch(new TTnaUpdateActions.SetUserPaginationOption(option));
  }
}

export type TGetTnaStepApprovalList = ReturnType<TnaApprovalsFacade['getTnaStepApprovalList']>;
export type TGetTnaStepApprovalDetails = ReturnType<TnaApprovalsFacade['getTnaStepApprovalDetails']>;
export type TGetTnaStepApprovalPendingList = ReturnType<TnaApprovalsFacade['getTnaStepApprovalPendingList']>;
export type TGetTnaStepApprovalPendingDetails = ReturnType<TnaApprovalsFacade['getTnaStepApprovalPendingDetails']>;
export type TGetEmployeesForApproval = ReturnType<TnaApprovalsFacade['fetchEmployeesForTnaApproval']>;
